import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ObjectPath from 'object-path';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import { H1 } from '@hobs/components/Headings';
import Toast from '@hobs/components/Toast';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Status from '@hobs/components/Status';
import Icon from '@hobs/components/Icon';
import Confirm from '@hobs/components/Confirm';
import { format } from '@hobs/utils/formatWithTimeZone';
import {
  USER_ROLES,
  APPROVAL_STATUSES,
  DATE_FORMAT
} from '@hobs/utils/constants';
import './ApprovalSummary.scss';
import Money from '@hobs/components/Money';

class ApprovalSummary extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isConfirmIsorDisabled: false,
      isConfirmFsorDisabled: false
    };
  }

  async componentDidMount() {
    await this.loadRequiredData();
  }

  componentWillUnmount() {
    this.unloadData();
  }

  unloadData() {
    this.props.crealSorParty();
    this.props.clearApprovalSummary();
  }

  async loadRequiredData() {
    const { sorId } = this.props.match.params;
    await this.props.getSor(sorId).then(async () => {
      await this.props.getSorParty(this.props.sor.sor.party.partyId);
    });
    this.loadApprovalSummary();
  }

  loadApprovalSummary() {
    const { getApprovalSummary, match } = this.props;
    const { sorId } = match.params;

    getApprovalSummary(sorId);
  }

  getPartyName() {
    return ObjectPath.get(this.props.sorParty, 'partyName', '');
  }

  isConfirmIsorEnabled = () => {
    const { sorParty } = this.props;
    const { isConfirmIsorDisabled } = this.state;
    return (
      !ObjectPath.get(sorParty, 'isConfirmIsorEnabled') || isConfirmIsorDisabled
    );
  };

  canUserConfirmIsor() {
    const { hasUserPermission } = this.props;
    return (
      hasUserPermission(USER_ROLES.ADMIN) ||
      hasUserPermission(USER_ROLES.SystemAdmin)
    );
  }

  handleConfirmIsorClick = () => {
    const { t } = this.props;

    if (!this.canUserConfirmIsor()) {
      Toast.warning(
        t(
          `You do not have permissions to confirm ISOR for ${this.getPartyName()}`
        )
      );
    } else {
      this.showConfirmIsorDialog();
    }
  };

  showConfirmIsorDialog() {
    Confirm(
      this.props.t('Confirm ISOR'),
      this.props.t(`This will confirm the complete INITIAL SOR for ALL units.
    Are you sure you want to deliver ISOR for ${this.getPartyName()} ?`),
      () => {
        this.handleIsorConfirmSubmit();
      }
    );
  }

  _confirmIsor() {
    const { confirmSor, sorParty } = this.props;
    return confirmSor(sorParty.statementOfRequirementId);
  }

  handleIsorConfirmSubmit = () => {
    const { t } = this.props;
    this._confirmIsor().then(response => {
      if (response.success === true) {
        Toast.success(t('ISOR confirmed successfully'));
        this.setState({
          isConfirmIsorDisabled: true
        });
        this.loadApprovalSummary();
      } else {
        Toast.warning(response.payload.message);
      }
    });
  };

  closeSorConfirmDialog = () => {
    this.setState({
      showConfirmDialog: false
    });
  };

  isConfirmFsorEnabled = () => {
    const { sorParty } = this.props;
    const { isConfirmFsorDisabled } = this.state;

    return (
      !ObjectPath.get(sorParty, 'isConfirmFsorEnabled') || isConfirmFsorDisabled
    );
  };

  canUserConfirmFsor() {
    const { hasUserPermission } = this.props;
    return (
      hasUserPermission(USER_ROLES.ADMIN) ||
      hasUserPermission(USER_ROLES.SystemAdmin)
    );
  }

  _validateConfirmFsor() {
    const { validateConfirmFsor, sorParty } = this.props;
    return validateConfirmFsor(sorParty.statementOfRequirementId);
  }

  handleConfirmFsorClick = () => {
    const { t } = this.props;

    if (!this.canUserConfirmFsor()) {
      Toast.warning(
        t(
          `You do not have permissions to confirm FSOR for ${this.getPartyName()}`
        )
      );
    } else {
      this._validateConfirmFsor().then(response => {
        if (response.success) {
          this.showConfirmFsorDialog();
        } else if (response.payload.message) {
          Toast.warning(response.payload.message);
        } else {
          Toast.warning(t('Cannot confirm fsor'));
        }
      });
    }
  };

  showConfirmFsorDialog() {
    Confirm(
      this.props.t('Confirm FSOR'),
      this.props.t(`This will confirm the complete FINAL SOR for ALL units.
      Are you sure you want to deliver FSOR for ${this.getPartyName()} ?`),
      () => {
        this.handleFsorConfirmSubmit();
      }
    );
  }

  _confirmFsor() {
    const { confirmFsor, sorParty } = this.props;
    return confirmFsor(sorParty.statementOfRequirementId);
  }

  handleFsorConfirmSubmit = () => {
    const { t } = this.props;

    this._confirmFsor().then(response => {
      if (response.success === true) {
        Toast.success(t('FSOR confirmed successfully'));
        this.setState({
          isConfirmFsorDisabled: true
        });
        this.loadApprovalSummary();
      } else {
        Toast.warning(response.payload.message);
      }
    });
  };

  canUserApproveUnit() {
    const { hasUserPermission } = this.props;
    return (
      hasUserPermission(USER_ROLES.ADMIN) ||
      hasUserPermission(USER_ROLES.SystemAdmin)
    );
  }

  _approveUnit(unitId) {
    const { approve, match } = this.props;
    const { sorId } = match.params;

    return approve(sorId, {
      unitId: unitId
    });
  }

  approveUnit(unit) {
    const { t } = this.props;
    if (this.canUserApproveUnit()) {
      Confirm(
        t('Approve service requests'),
        t(
          `Are you sure, you want to approve service requests for ${unit.unitName}?
        No further changes will be possible after approval.`
        ),
        () => {
          this._approveUnit(unit.unitId).then(response => {
            if (response.success === true) {
              Toast.success(t('Services approved successfully'));
            } else if (response.payload && response.payload.message) {
              Toast.warning(response.payload.message);
            } else {
              Toast.warning(t('Cannot approve services'));
            }
            this.loadApprovalSummary();
          });
        }
      );
    } else {
      Toast.warning(t('You do not have permissions to approve services'));
    }
  }

  canUserCancelUnitApproval() {
    const { hasUserPermission } = this.props;
    return (
      hasUserPermission(USER_ROLES.ADMIN) ||
      hasUserPermission(USER_ROLES.SystemAdmin)
    );
  }

  _cancelApproval(unitId) {
    const { cancelApproval, match } = this.props;
    const { sorId } = match.params;

    return cancelApproval(sorId, {
      unitId: unitId
    });
  }

  cancelUnitApproval(unit) {
    const { t } = this.props;

    if (this.canUserCancelUnitApproval()) {
      Confirm(
        t('Approve service requests'),
        t(`Are you sure, you want to cancel approval for ${unit.unitName}?`),
        () => {
          this._cancelApproval(unit.unitId).then(response => {
            if (response.success === true) {
              Toast.success(t('Approval removed successfully'));
            } else if (response.payload && response.payload.message) {
              Toast.warning(response.payload.message);
            } else {
              Toast.warning(t('Cannot remove approval'));
            }
            this.loadApprovalSummary();
          });
        }
      );
    } else {
      Toast.warning(t('You do not have permissions to approve services'));
    }
  }

  renderUnits() {
    const { t, units, currentExercise } = this.props;
    return units.map(unit => {
      return (
        <table className="table" key={unit.unitId}>
          {units.indexOf(unit) === 0 && (
            <thead>
              <tr>
                <th>{t('Element')}</th>
                <th className="status">{t('Status')}</th>
                <th>{t('Approved by')}</th>
                <th className="small">{t('Service requests')}</th>
                <th className="small">
                  {t('Est. cost')}
                  {` (${currentExercise.currency.name})`}
                </th>
                <th className="medium">
                  {t('Accumulated cost')}
                  {` (${currentExercise.currency.name})`}
                </th>
                <th></th>
              </tr>
            </thead>
          )}
          <tbody>{this.renderUnit(unit)}</tbody>
        </table>
      );
    });
  }

  renderUnit(unit) {
    const { t } = this.props;
    return (
      <>
        <tr>
          <td
            className={cx('unit-name', {
              unit: unit.unitTypeId === 1,
              'sub-unit': unit.unitTypeId === 2,
              component: unit.unitTypeId === 3
            })}
          >
            {unit.unitName}
          </td>
          <td className="status">
            <Status
              type={parseInt(unit.status.id, 10)}
              name={unit.status.name}
              statuses={APPROVAL_STATUSES}
            />
          </td>
          <td className="long">
            <span
              className={cx('approved-by', {
                'not-approved': unit.status.id === 0
              })}
            >
              {ObjectPath.get(unit, 'responsiblePerson.firstName')}{' '}
              {ObjectPath.get(unit, 'responsiblePerson.lastName')}{' '}
              {unit.approvalDate && (
                <span className="approval-date">
                  {format(unit.approvalDate, DATE_FORMAT)}
                </span>
              )}
            </span>
          </td>
          <td className="small">{unit.serviceRequestCount}</td>
          <Money
            component={'td'}
            className="small"
            amountRangeString={unit.estimatedCost}
          ></Money>
          <Money
            component={'td'}
            className="small"
            amountRangeString={unit.totalCost}
          ></Money>
          <td className="actions">
            {unit.status.id === 1 && (
              <GhostButton
                disabled={unit.isDisabled}
                onClick={() => {
                  this.cancelUnitApproval(unit);
                }}
              >
                {t('Cancel approval')}
              </GhostButton>
            )}
            {unit.status.id === 0 && (
              <RaisedButton
                disabled={unit.isDisabled}
                onClick={() => {
                  this.approveUnit(unit);
                }}
              >
                {t('Approve')}
              </RaisedButton>
            )}
          </td>
        </tr>
        {unit.units &&
          unit.units.length > 0 &&
          this.renderChildUnits(unit.units)}
      </>
    );
  }

  renderChildUnits(units) {
    return units.map(unit => {
      return this.renderUnit(unit);
    });
  }

  render() {
    const { t, units } = this.props;
    return (
      <div className="approval-summary">
        <H1>{t('Approval summary')}</H1>
        <div className="approval-summary--header">
          <div className="approval-summary--header--actions">
            <GhostButton
              disabled={this.isConfirmIsorEnabled()}
              onClick={this.handleConfirmIsorClick}
            >
              {t('Confirm ISOR')}
            </GhostButton>
            <GhostButton
              disabled={this.isConfirmFsorEnabled()}
              onClick={this.handleConfirmFsorClick}
              className="approval-summary--header--actions--fsor"
            >
              {t('Confirm FSOR')}
            </GhostButton>
          </div>
        </div>
        {units && units.length > 0 && (
          <div className="approval-summary--units">{this.renderUnits()}</div>
        )}
      </div>
    );
  }
}

ApprovalSummary.propTypes = {
  t: PropTypes.func,
  hasUserPermission: PropTypes.func,
  match: PropTypes.object,
  sorParty: PropTypes.object,
  getSorParty: PropTypes.func,
  confirmSor: PropTypes.func,
  confirmFsor: PropTypes.func,
  getApprovalSummary: PropTypes.func,
  units: PropTypes.array,
  approve: PropTypes.func,
  cancelApproval: PropTypes.func,
  validateConfirmFsor: PropTypes.func,
  clearApprovalSummary: PropTypes.func,
  crealSorParty: PropTypes.func,
  currentExercise: PropTypes.object
};

export default ApprovalSummary;
